/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { getInvalidFields, isEntityInvalid, PlainImmutablePageState } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';

import { first } from 'lodash';

import {
  CASH_FLOW_FORECAST_DETAILS_INIT,
  cashFlowForecastArr,
  CashFlowForecastDetails,
  CashFlowForecastJob,
  getCashFlowForecastJobId,
  setCashFlowForecastJobField,
} from '../../resources/cash-flow-forecast.model';

@Injectable()
export class CashFlowForecastDetailsFormState extends PlainImmutablePageState<CashFlowForecastDetails> {
  useJobSchedules$ = this.select(({ useJobSchedules }) => useJobSchedules);
  useVirtualSchedules$ = this.select(({ useVirtualSchedules }) => useVirtualSchedules);
  jobs$ = this.select(({ jobs }) => jobs);
  focusedCell$ = this.select(({ focusedCell }) => focusedCell);

  originalValue: CashFlowForecastDetails;

  constructor() {
    super(CASH_FLOW_FORECAST_DETAILS_INIT);
  }

  get templates(): AutocompleteItem[] {
    return this.value.templates;
  }

  get firstInvalidJob(): Maybe<CashFlowForecastJob> {
    if (this.value.useVirtualSchedules) {
      return first(this.value.jobs.filter(isEntityInvalid));
    }
  }

  get hasInvalidJob(): boolean {
    return isNotNil(this.firstInvalidJob);
  }

  get invalid(): boolean {
    return this.hasInvalidJob;
  }

  build(details: CashFlowForecastDetails): void {
    this.setMode(FormMode.EditMode);
    this.setValue(details);
    this.originalValue = this.value;
  }

  merge(details: CashFlowForecastDetails): void {
    this.setMode(FormMode.EditMode);
    this.setField('jobs', details.jobs);
    this.resetDirty();
    this.originalValue = details;
  }

  setUseJobSchedules(use: boolean): void {
    this.startChangeDetection();
    this.setFields({
      useJobSchedules: use,
      useVirtualSchedules: !use ? false : this.value.useVirtualSchedules,
    });
    this.setDirtyIfChanged();
  }

  setUseVirtualSchedules(use: boolean): void {
    this.setFieldWithDirty('useVirtualSchedules', use);
  }

  reset(): void {
    this.setValue(this.originalValue);
    this.resetDirty();
  }

  setJobField(jobId: ID, field: string, value: any): void {
    this.setFieldWithDirty(
      'jobs',
      cashFlowForecastArr.update(this.value.jobs, jobId, (job) => setCashFlowForecastJobField(job, field, value)));
  }

  focusInvalidCell(): void {
    const { firstInvalidJob } = this;

    if (firstInvalidJob) {
      const jobId = getCashFlowForecastJobId(firstInvalidJob);
      const invalidFields = getInvalidFields(firstInvalidJob);
      if (invalidFields.includes('templateId')) {
        this.setField('focusedCell', { id: jobId, field: 'templateId' });
      } else if (invalidFields.includes('startDate')) {
        this.setField('focusedCell', { id: jobId, field: 'startDate' });
      }
    }
  }

  resetFocusedCell(): void {
    this.setField('focusedCell', void 0);
  }
}
